<div class="p-5 md:p-8" *transloco="let t; prefix: 'hr-tool.detail'">
  <p-breadcrumb
    class="mb-4 block"
    data-testid="breadcrumb"
    [model]="breadcrumbItems()"
    [style]="{background: 'transparent', border: 'none', padding: '0'}"
  ></p-breadcrumb>

  @if (toolResource.isLoading()) {
    <app-loading />
  } @else {
    <div class="flex flex-col gap-8 lg:flex-row">
      <!-- LEFT: main content -->
      <div class="flex min-w-0 flex-1 flex-col gap-5">
        <!-- Title row -->
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="flex flex-col gap-2">
            @if (applicationTypeLabel(); as label) {
              <p-tag
                [value]="label"
                [rounded]="true"
                class="hr-type-tag"
                [attr.data-testid]="'tool-application-type'"
              />
            }
            <h1 class="m-0 text-2xl font-bold tracking-tight md:text-3xl" data-testid="tool-name">
              {{ hrTool()?.translatedName }}
            </h1>
          </div>
          <p-button
            icon="pi pi-arrow-right-arrow-left"
            [label]="'hrTool.detail.compare' | transloco"
            severity="secondary"
            size="small"
            (onClick)="addToComparison()"
            data-testid="tool-compare-button"
          />
        </div>

        <!-- Short description -->
        @if (hrTool()?.translatedDescription) {
          <div class="text-sm leading-relaxed text-muted-color">
            <app-safe-html-output
              [html]="hrTool()?.translatedDescription"
              [testDataId]="'tool-description'"
            />
          </div>
        }

        <!-- Image thumbnails (open a lightbox on click) -->
        @if (images().length) {
          <div class="flex flex-wrap gap-2" data-testid="tool-images">
            @for (image of images(); track image) {
              <p-image
                [src]="image"
                [alt]="hrTool()?.translatedName || 'HR Tool'"
                [preview]="true"
                (onImageError)="onImageError(image)"
                styleClass="hr-detail-thumb"
                imageClass="hr-detail-thumb__img"
              />
            }
          </div>
        }

        <!-- Detailed description -->
        @if (hrTool()?.translatedDetailedDescription) {
          <div class="card p-5">
            <app-safe-html-output
              class="text-sm leading-relaxed text-muted-color"
              [html]="hrTool()?.translatedDetailedDescription"
              [testDataId]="'detailed-description'"
            />
          </div>
        }

        <!-- Website -->
        @if (hrTool()?.website) {
          <div class="flex flex-wrap items-center gap-2 text-sm">
            <span class="font-semibold">{{ t('website') }}:</span>
            <a
              [href]="getWebsiteUrl(hrTool()?.website)"
              target="_blank"
              rel="noopener noreferrer"
              class="hr-link inline-flex items-center gap-1.5"
              data-testid="tool-website"
            >
              {{ hrTool()?.website }}
              <i class="pi pi-external-link text-xs"></i>
            </a>
          </div>
        }

        <!-- Functions checklist -->
        <p-accordion class="accordion-dark" [multiple]="true" [value]="['moduleFunctions']">
          <p-accordion-panel value="moduleFunctions">
            <p-accordion-header>
              <span class="text-base font-semibold" data-testid="module-functions-header">
                {{ hrTool()?.translatedName + ' ' + t('moduleFunctions') }}
              </span>
            </p-accordion-header>
            <p-accordion-content>
              @if (moduleFunctions().length > 0) {
                <div class="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                  @for (func of moduleFunctions(); track func.id + '_' + $index) {
                    <div
                      class="flex items-start gap-2 text-sm"
                      [attr.data-testid]="'module-function-' + func.id"
                    >
                      <i class="pi pi-check mt-0.5 shrink-0 text-xs text-emerald-500"></i>
                      <app-module-function-label
                        [translations]="func.translations!"
                        [wrap]="true"
                      />
                    </div>
                  }
                </div>
              } @else {
                <p class="text-muted-color" data-testid="no-module-functions">
                  {{ t('noModuleFunctions') }}
                </p>
              }
            </p-accordion-content>
          </p-accordion-panel>
        </p-accordion>

        <app-hr-tool-ai-profile-view [profile]="aiCapabilities()" [toolName]="toolName()" />
      </div>

      <!-- RIGHT: details sidebar -->
      <div class="lg:w-80 lg:shrink-0">
        <h3 class="mb-4 text-xl font-semibold" data-testid="sidebar-title">{{ t('details') }}</h3>

        <p-accordion
          [multiple]="true"
          [value]="[
            'applicationType',
            'serviceModels',
            'companySize',
            'certifications',
            'countries',
            'languages',
          ]"
        >
          <p-accordion-panel value="applicationType">
            <p-accordion-header [attr.data-testid]="'application-type-header'">
              {{ 'hrTool.applicationType.label' | transloco }}
            </p-accordion-header>
            <p-accordion-content>
              @if (applicationTypeLabel(); as label) {
                <p [attr.data-testid]="'application-type-value'">
                  {{ label }}
                </p>
              }
            </p-accordion-content>
          </p-accordion-panel>

          <p-accordion-panel value="serviceModels">
            <p-accordion-header [attr.data-testid]="'service-models-header'">
              {{ t('serviceModules') }}
            </p-accordion-header>
            <p-accordion-content>
              @if (serviceModels().length) {
                <ul class="list-none">
                  @for (model of serviceModels(); track model.id + '_' + $index) {
                    <li class="mb-2" [attr.data-testid]="'service-model-' + model.id">
                      {{ (model.translationKey | transloco) || model.name }}
                    </li>
                  }
                </ul>
              } @else {
                <p class="italic text-muted-color" [attr.data-testid]="'no-service-models'">
                  {{ t('noServiceModules') }}
                </p>
              }
            </p-accordion-content>
          </p-accordion-panel>

          <p-accordion-panel value="companySize">
            <p-accordion-header [attr.data-testid]="'company-size-header'">
              {{ 'hrTool.companySize' | transloco }}
            </p-accordion-header>
            <p-accordion-content>
              @if (companySize().min !== null || companySize().max !== null) {
                <p [attr.data-testid]="'company-size-value'">
                  @if (companySize().min !== null && companySize().max !== null) {
                    {{ companySize().min }} - {{ companySize().max }}
                    {{ 'hrTool.employees' | transloco }}
                  } @else if (companySize().min !== null) {
                    {{ 'hrTool.minEmployees' | transloco }}: {{ companySize().min }}
                    {{ 'hrTool.employees' | transloco }}
                  } @else if (companySize().max !== null) {
                    {{ 'hrTool.maxEmployees' | transloco }}: {{ companySize().max }}
                    {{ 'hrTool.employees' | transloco }}
                  }
                </p>
              } @else {
                <p class="italic text-muted-color" [attr.data-testid]="'no-company-size'">
                  {{ t('noCompanySize') }}
                </p>
              }
            </p-accordion-content>
          </p-accordion-panel>

          <p-accordion-panel value="certifications">
            <p-accordion-header [attr.data-testid]="'certifications-header'">
              {{ 'hrTool.certifications' | transloco }}
            </p-accordion-header>
            <p-accordion-content>
              <app-certification-list [certificationDirectusUuids]="certificationDirectusUuids()" />
            </p-accordion-content>
          </p-accordion-panel>

          <p-accordion-panel value="countries">
            <p-accordion-header data-testid="available-countries-header">
              {{ 'hrTool.availableCountries' | transloco }}
            </p-accordion-header>
            <p-accordion-content>
              @if (availableCountries().length) {
                <ul class="list-none space-y-2">
                  @for (country of availableCountries(); track country.isoCode + '_' + $index) {
                    <li
                      class="mb-2 flex items-center space-x-2"
                      [attr.data-testid]="'country-' + country.isoCode"
                    >
                      <img
                        [src]="getCountryFlagUrl(country.isoCode)"
                        alt="{{ getCountryDisplayName(country) }}"
                        class="h-3 w-4 shrink-0 object-contain"
                      />
                      <span>{{ getCountryDisplayName(country) }}</span>
                    </li>
                  }
                </ul>
              } @else {
                <p class="italic text-muted-color" data-testid="no-countries">
                  {{ t('noCountry') }}
                </p>
              }
            </p-accordion-content>
          </p-accordion-panel>

          <p-accordion-panel value="languages">
            <p-accordion-header [attr.data-testid]="'available-languages-header'">
              {{ 'hrTool.availableLanguages' | transloco }}
            </p-accordion-header>
            <p-accordion-content>
              @if (availableLanguages().length) {
                <ul class="list-none">
                  @for (language of availableLanguages(); track language.isoCode + '_' + $index) {
                    <li class="mb-2" [attr.data-testid]="'language-' + language.isoCode">
                      {{
                        language.translations
                          | directusTranslation: activeLanguage() : language.isoCode
                      }}
                    </li>
                  }
                </ul>
              } @else {
                <p class="italic text-muted-color" [attr.data-testid]="'no-languages'">
                  {{ t('noLanguage') }}
                </p>
              }
            </p-accordion-content>
          </p-accordion-panel>
        </p-accordion>
      </div>
    </div>

    <app-similar-tool class="mt-10 block" [excludeId]="hrTool()?.id || ''"></app-similar-tool>
  }
</div>
